import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { forwardRef, useRef } from "react";
import { u as useSpacing, a as useDisplay, b as useDimensions, d as useTypography, e as useBackground, f as useBorder, g as useAnimation, h as usePosition, i as useCursor, j as useResolvedAttributes, c as ce } from "./Grid-CcQKdsgp.js";
import { u as useUiKitComponents } from "./useUiKitComponents-CaMIXGvd.js";
import { useButton } from "@react-aria/button";
const buttonStyles = ce({
  base: "outline-none box-border border-0 flex justify-center items-center cursor-pointer transition-all duration-200 text-[var(--colors-text-primary-default)] font-noto-sans-sc",
  variants: {
    sizeButtonFontSize: {
      large: "[font-size:var(--fontSize-mobile-large-button)] md:[font-size:var(--fontSize-desktop-large-button)]",
      medium: "[font-size:var(--fontSize-mobile-medium-button)] md:[font-size:var(--fontSize-desktop-medium-button)]",
      small: "[font-size:var(--fontSize-mobile-small-button)] md:[font-size:var(--fontSize-desktop-small-button)]",
      xsmall: "[font-size:var(--fontSize-mobile-xsmall-button)] md:[font-size:var(--fontSize-desktop-xsmall-button)]"
    },
    sizeButtonLineHeight: {
      large: "[line-height:var(--lineHeight-mobile-large-button)] md:[line-height:var(--lineHeight-desktop-large-button)]",
      medium: "[line-height:var(--lineHeight-mobile-medium-button)] md:[line-height:var(--lineHeight-desktop-medium-button)]",
      small: "[line-height:var(--lineHeight-mobile-small-button)] md:[line-height:var(--lineHeight-desktop-small-button)]",
      xsmall: "[line-height:var(--lineHeight-mobile-xsmall-button)] md:[line-height:var(--lineHeight-desktop-xsmall-button)]"
    },
    variant: {
      default: "bg-[image:var(--colors-surface-primary-grad-default)] shadow-[0px_1px_1px_0px_var(--colors-surface-neutral-light)_inset,0px_-1px_1px_0px_var(--colors-surface-neutral-subtle)_inset,0px_4px_8px_0px_var(--colors-surface-neutral-dark-default)]hover:shadow-[0px_0px_13px_var(--colors-surface-destructive-default),0px_4px_8px_var(--colors-surface-neutral-subtle),inset_0px_-1px_1px_var(--colors-surface-destructive-subtle),inset_0px_1px_1px_var(--colors-surface-neutral-light)] active:shadow-[0px_0px_13px_var(--colors-surface-destructive-default),0px_4px_8px_var(--colors-surface-neutral-subtle),inset_0px_-1px_1px_var(--colors-surface-neutral-subtle),inset_0px_1px_1px_var(--colors-surface-neutral-light)] active:bg-[image:var(--colors-surface-primary-grad-pressed)]  hover:bg-[image:var(--colors-surface-primary-grad-hovered)]  ",
      transparent: "border border-[var(--colors-surface-secondary-default)] hover:bg-[var(--colors-surface-secondary-hovered)] hover:text-[var(--colors-text-primary-inverted)]",
      accent: "border-1 bg-[var(--colors-surface-secondary-default)] text-[var(--colors-text-primary-inverted)] ",
      secondary: "bg-[var(--colors-surface-neutral-light)]",
      disabled: "bg-[var(--colors-background-transparent)] text-[var(--colors-text-disabled)] cursor-not-allowed",
      tertiary: "",
      primary: "bg-[var(--colors-surface-primary-default)] hover:bg-[var(--colors-surface-primary-hovered)] active:bg-[var(--colors-surface-primary-subtle)]"
    },
    isActive: {
      true: "",
      false: ""
    },
    isDisabled: {
      true: "",
      false: ""
    }
  },
  compoundVariants: [
    {
      variant: "secondary",
      isActive: true,
      class: "border-[var(--colors-border-selected)] border text-[var(--colors-text-selection)]"
    },
    {
      variant: "default",
      isDisabled: true,
      class: "cursor-not-allowed pointer-events-none opacity-30"
    },
    {
      variant: "transparent",
      isDisabled: true,
      class: "cursor-not-allowed pointer-events-none opacity-30"
    },
    {
      variant: "accent",
      isDisabled: true,
      class: "cursor-not-allowed pointer-events-none opacity-30"
    },
    {
      variant: "secondary",
      isDisabled: true,
      class: "cursor-not-allowed pointer-events-none opacity-30"
    },
    {
      variant: "tertiary",
      isDisabled: true,
      class: "cursor-not-allowed pointer-events-none opacity-30"
    },
    {
      variant: "primary",
      isDisabled: true,
      class: "cursor-not-allowed pointer-events-none opacity-30"
    }
  ],
  defaultVariants: {
    variant: "default",
    isActive: false
  }
});
const DEFAULT_SPACINGS = {
  padding: {
    large: [16, 16, 16, 16],
    medium: [8, 8, 8, 8],
    small: [4, 4, 4, 4],
    xsmall: [2, 2, 2, 2]
  }
};
const Button = forwardRef(
  function({
    className,
    variant = "default",
    children,
    href,
    size = "medium",
    weight = "normal",
    p,
    m,
    d,
    md,
    hover,
    active,
    isActive = false,
    isDisabled = false,
    onPress,
    position,
    ...props
  }, ref) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r, _s, _t, _u, _v;
    const innerRef = useRef(null);
    const mergedRef = ref && typeof ref === "object" && "current" in ref ? ref : innerRef;
    const { buttonProps } = useButton(props, mergedRef);
    const { Link } = useUiKitComponents();
    const DEFAULT_SPACING_PADDING = DEFAULT_SPACINGS.padding[size != null ? size : "medium"];
    const spacing = useSpacing({
      m: { p, m },
      d,
      md,
      defaultPadding: DEFAULT_SPACING_PADDING
    });
    const display = useDisplay({
      m: { display: (_a = props.display) != null ? _a : "block" },
      md: { display: md == null ? void 0 : md.display },
      d: { display: d == null ? void 0 : d.display }
    });
    const dimension = useDimensions({ m: props, md, d });
    const fontWeight = weight ? `mobile.${weight}` : void 0;
    const mdFontWeight = (md == null ? void 0 : md.weight) !== weight && (md == null ? void 0 : md.weight) ? `desktop.${md == null ? void 0 : md.weight}` : void 0;
    const dFontWeight = (d == null ? void 0 : d.weight) !== (md == null ? void 0 : md.weight) && (d == null ? void 0 : d.weight) !== weight && (d == null ? void 0 : d.weight) ? `desktop.${d.weight}` : void 0;
    const typography = useTypography({
      m: { ...props, fontWeight: (_b = props.fontWeight) != null ? _b : fontWeight },
      md: {
        ...md,
        fontWeight: (_d = (_c = md == null ? void 0 : md.fontWeight) != null ? _c : props.fontWeight) != null ? _d : mdFontWeight
      },
      d: { ...d, fontWeight: (_f = (_e = d == null ? void 0 : d.fontWeight) != null ? _e : md == null ? void 0 : md.fontWeight) != null ? _f : dFontWeight },
      hover: { m: hover != null ? hover : {}, md: (_g = md == null ? void 0 : md.hover) != null ? _g : {}, d: (_h = d == null ? void 0 : d.hover) != null ? _h : {} }
    });
    const background = useBackground({
      m: {
        ...props,
        bgSize: (_i = props.bgSize) != null ? _i : "cover",
        bgRepeat: (_j = props.bgRepeat) != null ? _j : "no-repeat",
        bgPosition: (_k = props.bgPosition) != null ? _k : "center"
      },
      md: { ...md },
      d: { ...d },
      hover: {
        m: {
          ...hover
        },
        md: { ...md == null ? void 0 : md.hover },
        d: { ...d == null ? void 0 : d.hover }
      }
    });
    const border = useBorder({
      m: { radius: (_m = (_l = props.border) == null ? void 0 : _l.radius) != null ? _m : "button", ...props.border },
      md: md == null ? void 0 : md.border,
      d: d == null ? void 0 : d.border,
      hover: { m: hover == null ? void 0 : hover.border, md: (_n = md == null ? void 0 : md.hover) == null ? void 0 : _n.border, d: (_o = d == null ? void 0 : d.hover) == null ? void 0 : _o.border }
    });
    const animation = useAnimation({
      m: props,
      md,
      d,
      hover: { m: hover != null ? hover : {}, md: (_p = md == null ? void 0 : md.hover) != null ? _p : {}, d: (_q = d == null ? void 0 : d.hover) != null ? _q : {} },
      active: { m: active != null ? active : {}, md: (_r = md == null ? void 0 : md.active) != null ? _r : {}, d: (_s = d == null ? void 0 : d.active) != null ? _s : {} }
    });
    const positionStyles = usePosition({
      m: position ? { position } : void 0,
      md: (md == null ? void 0 : md.position) ? { position: md.position } : void 0,
      d: (d == null ? void 0 : d.position) ? { position: d.position } : void 0
    });
    const cursor = useCursor({ m: props, md, d });
    const resolvedProps = useResolvedAttributes(props);
    if (!href) {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        animation.keyFrames && /* @__PURE__ */ jsx("style", { children: animation.keyFrames }),
        /* @__PURE__ */ jsx(
          "button",
          {
            ...props,
            ...buttonProps,
            ...resolvedProps,
            ref: mergedRef,
            disabled: isDisabled,
            className: buttonStyles({
              variant,
              isDisabled,
              isActive,
              sizeButtonFontSize: props.fontSize ? void 0 : size,
              sizeButtonLineHeight: props.lineHeight ? void 0 : size,
              className: `${display.classNames} ${(_t = spacing.className) != null ? _t : ""} ${dimension.className} ${background.className} ${border.className} ${typography.classNames} ${cursor.className} ${animation.className} ${positionStyles.className} ${className != null ? className : ""}`
            }),
            onClick: (e) => onPress == null ? void 0 : onPress(e),
            style: {
              ...spacing.styles,
              ...dimension.styles,
              ...background.style,
              ...typography.styles,
              ...border.style,
              ...cursor.style,
              ...animation.style,
              ...positionStyles.styles
            },
            children
          }
        )
      ] });
    }
    if (href.includes("http")) {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        animation.keyFrames && /* @__PURE__ */ jsx("style", { children: animation.keyFrames }),
        /* @__PURE__ */ jsx(
          "a",
          {
            ...props,
            ...resolvedProps,
            className: buttonStyles({
              variant,
              isDisabled,
              isActive,
              sizeButtonFontSize: props.fontSize ? void 0 : size,
              sizeButtonLineHeight: props.lineHeight ? void 0 : size,
              className: `${display.classNames} ${(_u = spacing.className) != null ? _u : ""} ${dimension.className} ${background.className} ${border.className} ${typography.classNames} ${cursor.className} ${animation.className} ${positionStyles.className} ${className != null ? className : ""}`
            }),
            style: {
              ...spacing.styles,
              ...dimension.styles,
              ...background.style,
              ...typography.styles,
              ...border.style,
              ...cursor.style,
              ...animation.style,
              ...positionStyles.styles
            },
            href,
            target: "_blank",
            onClick: (e) => onPress == null ? void 0 : onPress(e),
            children
          }
        )
      ] });
    }
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      animation.keyFrames && /* @__PURE__ */ jsx("style", { children: animation.keyFrames }),
      /* @__PURE__ */ jsx(
        Link,
        {
          ...props,
          ...resolvedProps,
          href,
          className: buttonStyles({
            variant,
            isDisabled,
            isActive,
            sizeButtonFontSize: props.fontSize ? void 0 : size,
            sizeButtonLineHeight: props.lineHeight ? void 0 : size,
            className: `${display.classNames} ${(_v = spacing.className) != null ? _v : ""} ${dimension.className} ${border.className} ${background.className} ${typography.classNames} ${cursor.className} ${animation.className} ${positionStyles.className} ${className != null ? className : ""}`
          }),
          style: {
            ...spacing.styles,
            ...dimension.styles,
            ...background.style,
            ...typography.styles,
            ...border.style,
            ...cursor.style,
            ...animation.style,
            ...positionStyles.styles
          },
          onClick: (e) => onPress == null ? void 0 : onPress(e),
          children
        }
      )
    ] });
  }
);
export {
  Button as B
};
